// modules/EquipmentSystem.js
// 装备系统模块
import InventoryHelper from './InventoryHelper.js';
import ValidationHelper from './ValidationHelper.js';

export default class EquipmentSystem {
    /**
     * 装备槽位定义
     */
    static SLOTS = {
        WEAPON: 'weapon',
        HELMET: 'helmet',
        CAPE: 'cape',
        CHESTPLATE: 'chestplate',
        BRACER: 'bracer',
        GLOVE: 'glove',
        LEGPLATE: 'legplate',
        BOOT: 'boot'
    };
    
    /**
     * 装备槽位顺序（用于显示）
     */
    static SLOT_ORDER = [
        'helmet',    // 头部
        'cape',      // 肩
        'chestplate', // 胸
        'bracer',    // 手臂
        'glove',     // 手
        'legplate',  // 腿
        'boot',      // 脚
        'weapon'     // 武器
    ];
    
    /**
     * 装备槽位在人体剪影上的位置（相对坐标）
     */
    static SLOT_POSITIONS = {
        'helmet': { x: 0, y: -70 },      // 头部
        'cape': { x: 30, y: -20 },        // 肩
        'chestplate': { x: 0, y: -20 },  // 胸
        'bracer': { x: -30, y: -30 },     // 左手臂
        'glove': { x: -30, y: 0 },      // 左手
        'legplate': { x: 0, y: 40 },     // 腿
        'boot': { x: 0, y: 70 },         // 脚
        'weapon': { x: 30, y: 10 }       // 武器（右手）
    };
    
    /**
     * 穿戴装备
     * @param {Object} item - 装备对象
     * @param {Object} player - 玩家对象
     * @returns {Object} 穿戴结果 {success: boolean, message: string, oldEquipment?: Object}
     */
    static equipItem(item, player) {
        // 使用ValidationHelper验证数据
        const itemValidation = ValidationHelper.validateGameData(item, '装备');
        const playerValidation = ValidationHelper.validateGameData(player, '玩家');
        
        if (!itemValidation.valid || !playerValidation.valid) {
            return {
                success: false,
                message: itemValidation.message || playerValidation.message || '装备或玩家数据无效'
            };
        }
        
        // 检查是否为装备类型
        if (item.type !== 'equipment' && item.type !== 'weapon' && item.type !== 'armor' && item.type !== 'helmet') {
            return {
                success: false,
                message: '该物品不是装备'
            };
        }
        
        // 获取装备槽位
        let slot = item.slot || this.getSlotFromType(item.type);
        if (!slot) {
            return {
                success: false,
                message: '无法识别装备槽位'
            };
        }
        
        // 标准化槽位名称（将armor等旧槽位映射到新槽位）
        slot = this.normalizeSlot(slot);
        
        // 确保装备数组存在
        if (!player.equipment) {
            player.equipment = [];
        }
        
        // 查找当前槽位的装备
        const currentEquipped = this.getEquippedItem(player, slot);
        let oldEquipment = null;
        
        if (currentEquipped) {
            // 卸下当前装备
            oldEquipment = currentEquipped;
            this.unequipItem(player, slot);
        }
        
        // 穿戴新装备
        // 确保装备对象的slot属性被标准化
        const equippedItem = { ...item };
        equippedItem.slot = slot; // 使用标准化后的槽位
        
        // 找到槽位对应的索引，如果不存在则添加
        const slotIndex = this.getSlotIndex(player, slot);
        if (slotIndex !== -1) {
            player.equipment[slotIndex] = equippedItem;
        } else {
            player.equipment.push(equippedItem);
        }
        
        return {
            success: true,
            message: `已装备 ${item.name}`,
            oldEquipment: oldEquipment
        };
    }
    
    /**
     * 卸下装备
     * @param {Object} player - 玩家对象
     * @param {string} slot - 装备槽位
     * @returns {Object} 卸下结果 {success: boolean, message: string, item?: Object}
     */
    static unequipItem(player, slot) {
        if (!player || !slot) {
            return {
                success: false,
                message: '参数无效'
            };
        }
        
        if (!player.equipment) {
            return {
                success: false,
                message: '没有装备'
            };
        }
        
        const slotIndex = this.getSlotIndex(player, slot);
        if (slotIndex === -1) {
            return {
                success: false,
                message: '该槽位没有装备'
            };
        }
        
        const item = player.equipment[slotIndex];
        player.equipment[slotIndex] = null; // 或删除该元素
        
        // 清理空槽位
        player.equipment = player.equipment.filter(eq => eq !== null && eq !== undefined);
        
        return {
            success: true,
            message: `已卸下 ${item.name}`,
            item: item
        };
    }
    
    /**
     * 获取指定槽位的装备
     * @param {Object} player - 玩家对象
     * @param {string} slot - 装备槽位
     * @returns {Object|null} 装备对象
     */
    static getEquippedItem(player, slot) {
        if (!player || !player.equipment) return null;
        
        // 标准化槽位名称
        const normalizedSlot = this.normalizeSlot(slot);
        
        return player.equipment.find(item => {
            if (!item) return false;
            const itemSlot = item.slot ? this.normalizeSlot(item.slot) : this.getSlotFromType(item.type);
            return itemSlot === normalizedSlot;
        }) || null;
    }
    
    /**
     * 获取槽位在装备数组中的索引
     * @param {Object} player - 玩家对象
     * @param {string} slot - 装备槽位
     * @returns {number} 索引，-1表示未找到
     */
    static getSlotIndex(player, slot) {
        if (!player || !player.equipment) return -1;
        
        // 标准化槽位名称
        const normalizedSlot = this.normalizeSlot(slot);
        
        return player.equipment.findIndex(item => {
            if (!item) return false;
            const itemSlot = item.slot ? this.normalizeSlot(item.slot) : this.getSlotFromType(item.type);
            return itemSlot === normalizedSlot;
        });
    }
    
    /**
     * 从类型获取槽位
     * @param {string} type - 装备类型
     * @returns {string|null} 槽位名称
     */
    static getSlotFromType(type) {
        const typeMap = {
            'weapon': 'weapon',
            'armor': 'chestplate',  // armor映射到chestplate（胸甲）
            'helmet': 'helmet',
            'shield': 'shield',
            'equipment': null // 需要从slot属性获取
        };
        
        return typeMap[type] || null;
    }
    
    /**
     * 标准化槽位名称（将旧槽位名称映射到新槽位名称）
     * @param {string} slot - 槽位名称
     * @returns {string} 标准化后的槽位名称
     */
    static normalizeSlot(slot) {
        const slotMap = {
            'armor': 'chestplate',  // 将armor映射到chestplate
            'shield': 'bracer'      // 将shield映射到bracer（如果需要）
        };
        
        return slotMap[slot] || slot;
    }
    
    /**
     * 计算装备属性加成
     * @param {Object} player - 玩家对象
     * @returns {Object} 属性加成 {attack: number, defense: number}
     */
    static calculateEquipmentBonuses(player) {
        const bonuses = {
            attack: 0,
            defense: 0
        };
        
        if (!player || !player.equipment) {
            return bonuses;
        }
        
        player.equipment.forEach(item => {
            if (item) {
                if (item.damage) bonuses.attack += item.damage;
                if (item.defense) bonuses.defense += item.defense;
            }
        });
        
        return bonuses;
    }
    
    /**
     * 检查是否可以装备
     * @param {Object} item - 装备对象
     * @param {Object} player - 玩家对象
     * @returns {boolean} 是否可以装备
     */
    static canEquip(item, player) {
        if (!item || !player) return false;
        
        // 检查是否为装备类型
        if (item.type !== 'equipment' && 
            item.type !== 'weapon' && 
            item.type !== 'armor' && 
            item.type !== 'helmet') {
            return false;
        }
        
        // 检查是否有槽位信息
        let slot = item.slot || this.getSlotFromType(item.type);
        if (slot) {
            slot = this.normalizeSlot(slot);
        }
        return !!slot;
    }
    
    /**
     * 从背包中移除装备（穿戴时）
     * @param {Object} item - 装备对象
     * @param {Array} inventory - 背包数组
     * @returns {boolean} 是否移除成功
     */
    static removeFromInventory(item, inventory) {
        return InventoryHelper.removeFromInventory(item, inventory);
    }
    
    /**
     * 添加装备到背包（卸下时）
     * @param {Object} item - 装备对象
     * @param {Array} inventory - 背包数组
     * @returns {boolean} 是否添加成功
     */
    static addToInventory(item, inventory) {
        return InventoryHelper.addToInventory(item, inventory);
    }
    
    /**
     * 获取槽位中文名称
     * @param {string} slot - 槽位名称
     * @returns {string} 中文名称
     */
    static getSlotChineseName(slot) {
        const nameMap = {
            'weapon': '武器',
            'helmet': '头盔',
            'cape': '披风',
            'chestplate': '胸甲',
            'bracer': '护臂',
            'glove': '手套',
            'legplate': '护腿',
            'boot': '鞋子',
            'armor': '防具',  // 兼容旧数据
            'shield': '盾牌'  // 兼容旧数据
        };
        
        return nameMap[slot] || slot;
    }
}

